import { useState, useEffect } from 'react';
import { 
  Mail, 
  MessageSquare, 
  Save, 
  RotateCcw, 
  Info,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { getCompanyId } from '../../utils/tenantContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
// Removed unused select components
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

interface Template {
  subject: string;
  body: string;
  enabled: boolean;
}

interface AlertTemplateSet {
  email: Template;
  sms: Template;
}

interface TemplateSettings {
  [alertType: string]: AlertTemplateSet;
}

const ALERT_TYPES = [
  { id: 'well_offline', label: 'Well Offline', description: 'Triggered when a well stops reporting or status changes to down.' },
  { id: 'high_pressure', label: 'High Pressure', description: 'Triggered when tubing or casing pressure exceeds maximum threshold.' },
  { id: 'low_production', label: 'Low Production', description: 'Triggered when daily production drops below configured percentage.' },
  { id: 'tank_high_level', label: 'Tank High Level', description: 'Triggered when tank fluid level reaches high capacity mark.' },
  { id: 'pump_failure', label: 'Pump Failure', description: 'Triggered when pump status indicates failure or zero SPM.' },
  { id: 'missed_check', label: 'Missed Well Check', description: 'Triggered when a scheduled well check is overdue.' },
];

const DEFAULT_TEMPLATES: TemplateSettings = {
  well_offline: {
    email: {
      subject: 'URGENT: Well {{well_name}} is OFFLINE',
      body: 'Alert: Well {{well_name}} ({{api_number}}) has gone offline at {{time}}. \n\nLast Status: {{status}}\nLocation: {{location}}\n\nPlease investigate immediately.',
      enabled: true
    },
    sms: {
      subject: '',
      body: 'ALERT: {{well_name}} is OFFLINE. Status: {{status}}. Check immediately.',
      enabled: true
    }
  },
  high_pressure: {
    email: {
      subject: 'WARNING: High Pressure on {{well_name}}',
      body: 'Warning: High pressure detected on {{well_name}}.\n\nReading: {{value}} psi\nThreshold: {{threshold}} psi\nType: {{pressure_type}}\n\nTime: {{time}}',
      enabled: true
    },
    sms: {
      subject: '',
      body: 'WARN: High Pressure on {{well_name}}. {{value}} psi (Limit: {{threshold}}).',
      enabled: true
    }
  },
  low_production: {
    email: {
      subject: 'Production Alert: {{well_name}} Low Output',
      body: 'Production on {{well_name}} has dropped below target.\n\nCurrent: {{current_vol}} bbl\nTarget: {{target_vol}} bbl\nVariance: {{variance}}%\n\nPlease review pump performance.',
      enabled: true
    },
    sms: {
      subject: '',
      body: 'ALERT: {{well_name}} production low. {{current_vol}} bbl (Target: {{target_vol}}).',
      enabled: true
    }
  },
  tank_high_level: {
    email: {
      subject: 'CRITICAL: Tank High Level at {{location_name}}',
      body: 'Critical Alert: Tank {{tank_name}} at {{location_name}} is reaching capacity.\n\nLevel: {{level_feet}} ft ({{level_percent}}%)\nVolume: {{volume_bbl}} bbl\n\nDispatch hauler immediately to prevent spill.',
      enabled: true
    },
    sms: {
      subject: '',
      body: 'CRITICAL: Tank {{tank_name}} high level ({{level_percent}}%). Dispatch hauler ASAP.',
      enabled: true
    }
  },
  pump_failure: {
    email: {
      subject: 'EQUIPMENT FAILURE: Pump Down on {{well_name}}',
      body: 'Pump failure detected on {{well_name}}.\n\nStatus: {{status}}\nSPM: {{spm}}\nAmps: {{amps}}\n\nMaintenance ticket has been auto-created.',
      enabled: true
    },
    sms: {
      subject: '',
      body: 'FAILURE: Pump down on {{well_name}}. SPM: 0. Ticket created.',
      enabled: true
    }
  },
  missed_check: {
    email: {
      subject: 'MISSED CHECK: {{well_name}} Overdue',
      body: 'Scheduled check for {{well_name}} is overdue.\n\nAssigned To: {{pumper_name}}\nDue Date: {{due_date}}\n\nPlease ensure this well is inspected today.',
      enabled: true
    },
    sms: {
      subject: '',
      body: 'OVERDUE: {{well_name}} check missed. Assigned: {{pumper_name}}.',
      enabled: true
    }
  }
};

const VARIABLES = [
  { name: '{{well_name}}', desc: 'Name of the well' },
  { name: '{{api_number}}', desc: 'API Number' },
  { name: '{{time}}', desc: 'Time of event' },
  { name: '{{value}}', desc: 'Sensor reading value' },
  { name: '{{threshold}}', desc: 'Alert threshold limit' },
  { name: '{{location}}', desc: 'GPS or Lease location' },
  { name: '{{pumper_name}}', desc: 'Assigned pumper' },
];

export default function NotificationTemplateEditor() {
  const [selectedType, setSelectedType] = useState<string>('well_offline');
  const [templates, setTemplates] = useState<TemplateSettings>(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeChannel, setActiveChannel] = useState('email');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    try {
      const docRef = doc(db, `companies/${companyId}/settings`, 'notification_templates');
      const snap = await getDoc(docRef);
      
      if (snap.exists()) {
        // Merge with defaults to ensure all types exist
        setTemplates({ ...DEFAULT_TEMPLATES, ...snap.data() });
      } else {
        setTemplates(DEFAULT_TEMPLATES);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    setSaving(true);
    try {
      const docRef = doc(db, `companies/${companyId}/settings`, 'notification_templates');
      await setDoc(docRef, templates);
      toast.success('Templates saved successfully');
    } catch (error) {
      console.error('Failed to save templates:', error);
      toast.error('Failed to save templates');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this template to default?')) {
      setTemplates(prev => ({
        ...prev,
        [selectedType]: DEFAULT_TEMPLATES[selectedType]
      }));
      toast.info('Template reset to default');
    }
  };

  const updateTemplate = (field: 'subject' | 'body' | 'enabled', value: any) => {
    setTemplates(prev => ({
      ...prev,
      [selectedType]: {
        ...prev[selectedType],
        [activeChannel]: {
          ...prev[selectedType][activeChannel as 'email' | 'sms'],
          [field]: value
        }
      }
    }));
  };

  const currentTemplate = templates[selectedType]?.[activeChannel as 'email' | 'sms'] || { subject: '', body: '', enabled: false };
  const currentTypeInfo = ALERT_TYPES.find(t => t.id === selectedType);

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Templates</h1>
          <p className="text-gray-500">Customize the content of automated alerts sent to your team.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Sidebar: Alert Types */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Alert Types</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {ALERT_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    selectedType === type.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                  }`}
                >
                  <div>
                    <div className={`font-medium ${selectedType === type.id ? 'text-blue-700' : 'text-gray-700'}`}>
                      {type.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">
                      {type.description}
                    </div>
                  </div>
                  {selectedType === type.id && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Main Editor Area */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-4 border-b">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>{currentTypeInfo?.label}</CardTitle>
                <CardDescription className="mt-1">{currentTypeInfo?.description}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={handleReset} title="Reset to Default">
                <RotateCcw className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <Tabs value={activeChannel} onValueChange={setActiveChannel} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="email" className="flex items-center gap-2 border bg-white text-blue-700 data-[state=active]:border-blue-600 data-[state=active]:shadow-sm">
                  <Mail className="h-4 w-4" /> Email Template
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex items-center gap-2 border bg-white text-green-700 data-[state=active]:border-green-600 data-[state=active]:shadow-sm">
                  <MessageSquare className="h-4 w-4" /> SMS Template
                </TabsTrigger>
              </TabsList>

              <div className="space-y-4">
                <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${currentTemplate.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-sm font-medium text-gray-700">
                      {activeChannel === 'email' ? 'Email Notifications' : 'SMS Notifications'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{currentTemplate.enabled ? 'Enabled' : 'Disabled'}</span>
                    <button
                      onClick={() => updateTemplate('enabled', !currentTemplate.enabled)}
                      className={`
                        relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                        ${currentTemplate.enabled ? 'bg-blue-600' : 'bg-gray-300'}
                      `}
                      aria-label={currentTemplate.enabled ? 'Disable notifications' : 'Enable notifications'}
                      title={currentTemplate.enabled ? 'Disable notifications' : 'Enable notifications'}
                    >
                      <span className={`
                        inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                        ${currentTemplate.enabled ? 'translate-x-5' : 'translate-x-1'}
                      `} />
                    </button>
                  </div>
                </div>

                {activeChannel === 'email' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Subject Line</label>
                    <Input 
                      value={currentTemplate.subject}
                      onChange={(e) => updateTemplate('subject', e.target.value)}
                      placeholder="Enter email subject..."
                      disabled={!currentTemplate.enabled}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    {activeChannel === 'email' ? 'Email Body' : 'SMS Message Content'}
                  </label>
                  <Textarea 
                    value={currentTemplate.body}
                    onChange={(e) => updateTemplate('body', e.target.value)}
                    placeholder={activeChannel === 'email' ? 'Enter email content...' : 'Enter SMS message...'}
                    className="min-h-[200px] font-mono text-sm"
                    disabled={!currentTemplate.enabled}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Supports markdown formatting</span>
                    {activeChannel === 'sms' && (
                      <span className={currentTemplate.body.length > 160 ? 'text-orange-600 font-medium' : ''}>
                        {currentTemplate.body.length} / 160 characters (approx. {Math.ceil(currentTemplate.body.length / 160)} segments)
                      </span>
                    )}
                  </div>
                </div>

                {/* Variables Helper */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 mb-2 text-blue-800 font-medium text-sm">
                    <Info className="h-4 w-4" />
                    Available Variables
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {VARIABLES.map(v => (
                      <Badge 
                        key={v.name} 
                        variant="outline" 
                        className="bg-white hover:bg-blue-100 cursor-pointer border-blue-200 text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        onClick={() => {
                          if (!currentTemplate.enabled) return;
                          const field = activeChannel === 'email' && document.activeElement?.tagName === 'INPUT' ? 'subject' : 'body';
                          const currentValue = currentTemplate[field];
                          updateTemplate(field, currentValue + ' ' + v.name);
                          toast.success(`Added ${v.name}`);
                        }}
                        title={v.desc}
                      >
                        {v.name}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Click a variable to append it to your message.
                  </p>
                </div>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
